import styled from 'styled-components';
import { getTheme } from './getTheme';

/**
 * Common HTML elements we want to support. Creating this type explicitly
 * seems to noticeably speed up TypeScript intellisense.
 */

// From Styled Components:
// https://github.com/styled-components/styled-components/blob/8b9d66f9a8e1dc59cfe5f5c4c079d8fcf5054e12/packages/styled-components/src/utils/domElements.ts
const ELEMENTS = ['a', 'abbr', 'address', 'area', 'article', 'aside', 'audio', 'b', 'base', 'bdi', 'bdo', 'big', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'cite', 'code', 'col', 'colgroup', 'data', 'datalist', 'dd', 'del', 'details', 'dfn', 'dialog', 'div', 'dl', 'dt', 'em', 'embed', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header', 'hgroup', 'hr', 'html', 'i', 'iframe', 'img', 'input', 'ins', 'kbd', 'keygen', 'label', 'legend', 'li', 'link', 'main', 'map', 'mark', 'menu', 'menuitem', 'meta', 'meter', 'nav', 'noscript', 'object', 'ol', 'optgroup', 'option', 'output', 'p', 'param', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'script', 'section', 'select', 'small', 'source', 'span', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'textarea', 'tfoot', 'th', 'thead', 'time', 'tr', 'track', 'u', 'ul', 'use', 'var', 'video', 'wbr',
// SVG
'circle', 'clipPath', 'defs', 'ellipse', 'foreignObject', 'g', 'image', 'line', 'linearGradient', 'marker', 'mask', 'path', 'pattern', 'polygon', 'polyline', 'radialGradient', 'rect', 'stop', 'svg', 'text', 'tspan'];

// Define an opaque type for Theme to compress error messages in TS

/**
 * Creates a styled-component factory function for a specific HTML element.
 *
 * @template K - The HTML element key (e.g., 'div', 'span', etc.)
 * @param tag - The HTML element tag to create a styled component for
 * @returns A function that accepts styling props and returns a styled component.
 *          - P: Custom props type (default: {})
 *          - T: Theme type (default: Theme, might support others in the future)
 *          The returned function creates a styled component with proper
 *          theme and props typing.
 */
const createElement = tag => {
  const styledElement = styled(tag);
  const styledWithThemeElement = (first, ...args) => {
    // If called with template literals
    if (Array.isArray(first)) {
      return styledElement(first, ...args);
    }

    // If called as a function
    return styledElement(props => {
      const theme = props.theme || getTheme({
        theme: props.theme
      });
      return first(Object.assign({}, props, {
        theme
      }));
    });
  };

  // Copy over all styled-components methods
  Object.assign(styledWithThemeElement, styledElement);
  return styledWithThemeElement;
};

/**
 * Creates an object containing styled component factories for common HTML
 * elements. Each factory supports custom props and theme customization
 * through generics.
 *
 * @returns An object where each key is an HTML element name and each value is a styled component factory.
 *          The factories accept:
 *          - P: Custom props type (default: {})
 *          - T: Theme type (default: Theme)
 *
 * @example
 * ```tsx
 * // Call with default theme, no custom props
 * styledWithTheme.div(({ theme }) => {
 *   return {
 *     color: theme.color['text-core-default'],
 *   };
 * });
 *
 * // Call with default theme and custom props
 * styledWithTheme.div<CustomProps>(({ theme, $customProp }) => {
 *   console.log($customProp);
 *
 *   return {
 *     color: theme.color['text-core-default'],
 *   };
 * });
 * ```
 */
const createStyledWithTheme = () => {
  let elements = {};
  ELEMENTS.forEach(tag => {
    elements[tag] = createElement(tag);
  });

  // Add function to style custom components
  elements = Object.assign(createElement, elements);
  return elements;
};
export const styledWithTheme = createStyledWithTheme();